import { Amplify } from 'aws-amplify';
import { signIn, signUp, signOut, confirmSignUp, fetchAuthSession } from 'aws-amplify/auth';

class AuthService {
  constructor() {
    this.configured = false;
  }

  /**
   * Configure Amplify with the user pool credentials
   * Call this once at app startup with your AgentParent user pool
   */
  configure(userPoolId, userPoolClientId, region) {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: userPoolId,
          userPoolClientId: userPoolClientId,
          region: region,
        }
      }
    });
    this.configured = true;
  }

  /**
   * Sign up a new user
   */
  async signUp(username, password, email) {
    if (!this.configured) {
      throw new Error('Auth not configured. Call configure() first.');
    }

    try {
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });

      return {
        isSignUpComplete,
        userId,
        nextStep,
      };
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  }

  /**
   * Verify email with confirmation code
   */
  async verifyEmail(username, code) {
    if (!this.configured) {
      throw new Error('Auth not configured. Call configure() first.');
    }

    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username,
        confirmationCode: code,
      });

      return {
        isSignUpComplete,
        nextStep,
      };
    } catch (error) {
      console.error('Error confirming sign up:', error);
      throw error;
    }
  }

  /**
   * Sign in a user and return the access token
   */
  async signIn(username, password) {
    if (!this.configured) {
      throw new Error('Auth not configured. Call configure() first.');
    }

    try {
      const { isSignedIn, nextStep } = await signIn({
        username,
        password,
      });

      // Get the session and access token
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      return {
        isSignedIn,
        nextStep,
        accessToken,
      };
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  }

  /**
   * Sign out the current user
   */
  async signOut() {
    if (!this.configured) {
      throw new Error('Auth not configured. Call configure() first.');
    }

    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  /**
   * Get the current user's session and tokens
   */
  async getCurrentSession() {
    if (!this.configured) {
      throw new Error('Auth not configured. Call configure() first.');
    }

    try {
      const session = await fetchAuthSession();
      return session;
    } catch (error) {
      console.error('Error getting session:', error);
      throw error;
    }
  }

  /**
   * Get the current access token
   */
  async getAccessToken() {
    const session = await this.getCurrentSession();
    return session.tokens?.accessToken?.toString();
  }

  /**
   * Get the current ID token
   */
  async getIdToken() {
    const session = await this.getCurrentSession();
    return session.tokens?.idToken?.toString();
  }

  /**
   * Call MCP gateway tools/list
   */
  async listTools(gatewayUrl) {
    const accessToken = await this.getAccessToken();
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    };

    const payload = {
      jsonrpc: '2.0',
      id: 'list-tools-request',
      method: 'tools/list',
    };

    try {
      const response = await fetch(gatewayUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error listing tools:', error);
      throw error;
    }
  }

  /**
   * Call MCP gateway with a tool
   */
  async callTool(gatewayUrl, toolName, params = {}) {
    const accessToken = await this.getAccessToken();
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    };

    const payload = {
      jsonrpc: '2.0',
      id: `call-${toolName}-${Date.now()}`,
      method: 'tools/call',
      params: {
        name: toolName,
        arguments: params,
      },
    };

    try {
      const response = await fetch(gatewayUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error calling tool:', error);
      throw error;
    }
  }
}

export default new AuthService();
